import isPlainObject from 'lodash/isPlainObject'
import useJwt from '@/auth/jwt/useJwt'
import AuthApis from '@/API/auth-api'
import FreshchatService from '@/services/FreshchatService'
import ErrorTrackingService from '@/services/error-tracking-service'
import HotjarService from '@/services/hotjar-service'
import StorageService from '@/services/storage-service'
import MarketingService from '@/services/marketing-service'
import AccountsAPI from '@/API/users/accounts-api'
// import convertImageToCanvas from '@/utils/convert-image'

export default {
  namespaced: true,
  state: {
    user: null,
    token: null,
    mfaToken: null,
    mfaMode: null,
    mfaOneTime: false,
    impersonate: false,
    utm: null,
    utms: [],
    wp_token: null,
  },
  getters: {
    isAuthenticated: (state: any) => !!state.user,
    isIncreasedRounding: (state: any) => state.user?.increase_rounding ?? false,
    decimals: (state: any, getters: any) => (getters.isIncreasedRounding ? 3 : 2),
    unreadAnnouncement: (state: any) => state.user?.unreadAnnouncement,
    subAccounts: (state: any) => state.user?.sub_accounts ?? [],
    mainAccount: (state: any) => state?.user?.main_account ?? localStorage.getItem('main_account'),
    user: (state: any) => state.user ?? {},
    receiptSettings: (state: any, getters: any) => getters.user.receipt_settings ?? {},
    token: (state: any) => state.token ?? '',
    mfaToken: (state: any) => state.mfaToken,
    mfaMode: (state: any) => state.mfaMode,
    mfaOneTime: (state: any) => state.mfaOneTime,
    isPrimaryAccount: (state: any) => state.user?.primaryAccount ?? true,
    isSubUser: (state: any) => !(state.user?.primaryAccount ?? true),
    impersonate: (state: any) => state.impersonate,
    utms: (state: any) => state.utms,
    userUtm: (state: any) => state.utm,
    ipadThemeId: (state: any) => state.user?.ipad_web_theme_id ?? null,
    mobileThemeId: (state: any) => state.user?.mobile_web_theme_id ?? null,
    wpToken: (state: any) => state.wp_token,
    defaultSupplierId: (state: any) => state.user?.default_supplier_id ?? null,
  },
  mutations: {
    SET_USER(state: any, user: unknown) {
      state.user = user
    },
    SET_UTM(state: any, utm: unknown) {
      if (utm && isPlainObject(utm)) {
        state.utm = utm ?? state.utm
      }
    },
    SET_TOKEN(state: any, token: unknown) {
      state.token = token
    },
    SET_MFA(state: any, { token, mode, oneTime }: any) {
      state.mfaToken = token || null
      state.mfaMode = mode || null
      state.mfaOneTime = !!oneTime
    },
    CLEAR_MFA(state: any) {
      state.mfaToken = null
      state.mfaMode = null
      state.mfaOneTime = false
    },
    UPDATE_USER(state: any, user: any) {
      state.user = {
        ...state.user,
        ...user,
      }
    },
    SET_IMPERSONATE(state: any, impersonate: boolean) {
      state.impersonate = impersonate
    },
    SET_UTMS(state: any, utms: any) {
      state.utms = utms
    },
    SET_WHATSAPP_TOKEN(state: any, token: any) {
      state.wp_token = token
    },
    SET_SUBACCOUNTS(state: any, subAccounts: any[]) {
      state.user.sub_accounts = subAccounts
    },
  },
  actions: {
    async LOGIN(context: any, { email, password, oneTime }: any) {
      const response = (await useJwt.login({ email, password })).data
      if (response?.mfa_setup_required || response?.mfa_required) {
        if (!response?.mfa_token) {
          return { mfaPending: false, invalidMfa: true }
        }
        // Token stays in Vuex only so a refresh drops the unfinished challenge.
        context.commit('SET_MFA', {
          token: response.mfa_token,
          mode: response.mfa_setup_required ? 'setup' : 'challenge',
          oneTime,
        })
        return {
          mfaPending: true,
          mode: response.mfa_setup_required ? 'setup' : 'challenge',
        }
      }
      const user = await context.dispatch('FINISH_LOGIN', { response, oneTime })
      return { mfaPending: false, user }
    },
    async FINISH_LOGIN(context: any, { response, oneTime }: any) {
      const rememberOnce = oneTime ?? context.state.mfaOneTime
      await context.dispatch('PREPARE_USER', { ...response, oneTime: !!rememberOnce })
      if (response?.user?.main_account) {
        localStorage.setItem('main_account', response?.user?.main_account)
      } else {
        localStorage.removeItem('main_account')
      }
      MarketingService.sendUtm()
      context.commit('CLEAR_MFA')
      return response.user
    },
    CLEAR_MFA(context: any) {
      context.commit('CLEAR_MFA')
    },
    async REGISTER(context: any, payload: any) {
      const response = (await useJwt.register(payload)).data
      // await context.dispatch('PREPARE_USER', response)
      MarketingService.sendUtm()
      return response.user
    },
    async LOGOUT(context: any) {
      if (context.state.impersonate) {
        context.commit('SET_IMPERSONATE', false)
        return
      }
      await useJwt.logout(context.state.user?.id)
      StorageService.removeItem('okmenu_last_purchase')
      //      context.commit('SET_USER', null)
      //      context.commit('branches/RESET', null, { root: true })
    },
    async FETCH_USER_INFO(context: any) {
      context.commit('branches/RESET', null, { root: true })
      const token: any = useJwt.getToken()
      return context.dispatch('CHECK_TOKEN', { token, impersonate: context.state.impersonate })
    },
    async CHECK_TOKEN(context: any, { token, impersonate = false }: any) {
      const response = (await useJwt.checkToken(token)).data
      await context.dispatch('PREPARE_USER', { ...response, oneTime: impersonate })
      if (impersonate) {
        context.commit('SET_IMPERSONATE', true)
      }
      return response?.user
    },
    async socialLogin(commit: any, payload: any) {
      const providerResponse = { ...payload.providerResponse, phone: payload.phone }
      const response = await AuthApis.social(payload.provider, providerResponse)
      commit.dispatch('PREPARE_USER', response)
      MarketingService.sendUtm()
      return response.user
    },
    async PREPARE_USER(context: any, {
      device, user, admin_permissions, subscription, oneTime, utm, is_pos_enabled, permissions, subuser_permissions,
    }: any) {
      if (!user) throw new Error('Set user failed')
      if (!oneTime) {
        useJwt.setToken(device.oauth_token)
        useJwt.setRefreshToken(device.refresh_token)
      }
      try {
        const logo = user.receipt_logo_base64 || user.receipt_logo
        if (logo) {
          // eslint-disable-next-line no-param-reassign
          // user.receipt_logo_context = await convertImageToCanvas(document.getElementById('receipt-logo-canvas'), logo)
        }
      } catch (e) {
        console.error(e)
      }
      context.commit('SET_USER', user)
      context.commit('SET_UTM', utm)
      context.commit('SET_TOKEN', device.oauth_token)
      context.commit('permissions/SET_PERMISSIONS', { admin_permissions, user }, { root: true })
      context.commit('branches/SET_BRANCHES', user?.branches ?? [], { root: true })
      context.commit('menu/SET_STORAGE', { availableStorage: user.storage, totalStorage: subscription.storage }, { root: true })
      context.commit('subscription/SET_SUBSCRIPTION', subscription ?? {}, { root: true })
      context.commit('businessTypes/SET_BUSINESS_TYPE', user.businessType, { root: true })
      context.commit('subusers/SET_TOTAL_USERS', subscription?.total_subusers ?? subscription?.total_users, { root: true })
      context.commit('pos/SET_ENABLE_STATUS', is_pos_enabled ?? true, { root: true })
      context.commit('permissions/inventoryPermissions', permissions, { root: true })
      if (user?.primaryAccount) {
        context.commit('permissions/RESET_SUBUSER_PERMISSIONS', null, { root: true })
      } else {
        context.commit('permissions/SET_SUBUSER_PERMISSIONS', subuser_permissions ?? user?.subuser_permissions ?? {}, { root: true })
      }
      try {
        context.dispatch('branches/CONNECT_PRINTER', null, { root: true })
      } catch (e) {
        console.error(e)
      }
      FreshchatService.setFreshchatInfo(user)
      ErrorTrackingService.setUser(user)
      HotjarService.setIdentity(user)
    },
    async FETCH_UTMS(context: any) {
      await AccountsAPI.getUtms()
        .then(data => (context.commit('SET_UTMS', data.utms)))
        .catch(() => null)
    },

    async SWITCH_ACCOUNT(context: any, payload: any) {
      const res = await AccountsAPI.switchAccount(payload)
        .then(response => {
          context.dispatch('PREPARE_USER', { ...response })
          if (response?.user?.main_account) {
            localStorage.setItem('main_account', response?.user?.main_account)
          } else {
            localStorage.removeItem('main_account')
          }
          return response
        })
        .catch(() => null)
      return res
    },
    async DELETE_SUB_ACCOUNT(context: any, payload: any) {
      return AccountsAPI.deleteSub(payload)
    },
    async TOGGLE_NOTIFICATIONS(context: any, payload: any) {
      context.commit('UPDATE_USER', payload)
      return AccountsAPI.toggleNotifications(payload)
    },
  },
}
